import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const VACCINES_URL = `${BASE_URL}/api/vaccines`;
const SCHEDULES_URL = `${BASE_URL}/api/vaccine-schedules`;
const TASKS_URL = `${BASE_URL}/api/vaccine-tasks`;

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizeEnum(value) {
  return value ? String(value).trim().toUpperCase().replace(/[\s-]+/g, "_") : undefined;
}

function normalizeDate(value) {
  if (!value) return undefined;
  if (typeof value === "string") return value.slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

function maybeNumber(v) {
  if (v === "" || v === undefined || v === null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeVaccinePayload(payload = {}) {
  const doseBasis = normalizeEnum(payload.doseBasis || "FIXED");
  const doseAmount = maybeNumber(payload.doseAmount ?? payload.ifFixed ?? payload.ifPerKg ?? payload.ifPerMl);
  const hasBooster = Boolean(payload.hasBooster);
  const hasRepeat = Boolean(payload.hasRepeat);

  const body = {
    ...payload,
    species: normalizeEnum(payload.species),
    projectType: normalizeEnum(payload.projectType || payload.batchGoal),
    name: payload.name || payload.vaccineName,
    vaccineName: payload.vaccineName || payload.name,
    doseBasis,
    doseAmount,
    doseUnit: normalizeEnum(payload.doseUnit || "ML"),
    doseRoute: normalizeEnum(payload.doseRoute || "INJECTION_MUSCLE"),
    minAgeDays: maybeNumber(payload.minAgeDays ?? payload.minAgeFromPurchaseDays),
    minAgeFromPurchaseDays: maybeNumber(payload.minAgeDays ?? payload.minAgeFromPurchaseDays),
    hasBooster,
    boosterAfterDays: hasBooster ? maybeNumber(payload.boosterAfterDays ?? payload.boosterDays) : undefined,
    boosterDays: hasBooster ? maybeNumber(payload.boosterAfterDays ?? payload.boosterDays) : undefined,
    hasRepeat,
    repeatEveryDays: hasRepeat ? maybeNumber(payload.repeatEveryDays ?? payload.repeatIntervalDays) : undefined,
    repeatIntervalDays: hasRepeat ? maybeNumber(payload.repeatEveryDays ?? payload.repeatIntervalDays) : undefined,
    storageTemp: payload.storageTemp || "",
    notes: payload.notes || "",
    active: payload.active ?? payload.isActive ?? true,
    isActive: payload.active ?? payload.isActive ?? true,
  };

  if (doseBasis === "PER_KG") body.ifPerKg = doseAmount;
  else if (doseBasis === "PER_ML") body.ifPerMl = doseAmount;
  else body.ifFixed = doseAmount;

  delete body.batchGoal;
  return body;
}

function normalizeSchedulePayload(payload = {}) {
  const applyTo = normalizeEnum(payload.applyTo || (payload.applyToAllOngoingBatches === false ? "SPECIFIC" : "ALL"));
  return {
    vaccineId: maybeNumber(payload.vaccineId),
    species: normalizeEnum(payload.species),
    projectType: normalizeEnum(payload.projectType || payload.batchGoal),
    applyTo,
    batchId: applyTo === "SPECIFIC" ? maybeNumber(payload.batchId) : undefined,
    firstDoseDate: normalizeDate(payload.firstDoseDate || payload.startDate),
    startDate: normalizeDate(payload.firstDoseDate || payload.startDate),
    applyToAllOngoingBatches: applyTo === "ALL",
  };
}

function logAxiosError(label, err) {
  console.error(label, err);
  if (err?.response) {
    console.error(`${label} response status:`, err.response.status);
    console.error(`${label} response data:`, err.response.data);
  }
}

// ---------------- Vaccines ----------------

export async function listVaccines({ species, projectType, isActive, active } = {}) {
  const params = {};
  if (species) params.species = normalizeEnum(species);
  if (projectType) params.projectType = normalizeEnum(projectType);
  if (isActive !== undefined && isActive !== "") params.isActive = isActive;
  if (active !== undefined && active !== "") params.active = active;

  try {
    const res = await axios.get(VACCINES_URL, { params, headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("listVaccines error", err);
    throw err;
  }
}

export async function createVaccine(payload) {
  try {
    const res = await axios.post(VACCINES_URL, normalizeVaccinePayload(payload), {
      headers: { ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    logAxiosError("createVaccine error", err);
    throw err;
  }
}

export async function updateVaccine(id, payload) {
  try {
    const res = await axios.put(`${VACCINES_URL}/${id}`, normalizeVaccinePayload(payload), {
      headers: { ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    logAxiosError("updateVaccine error", err);
    throw err;
  }
}

export async function setVaccineActive(id, active) {
  try {
    const res = await axios.patch(`${VACCINES_URL}/${id}/${active ? "enable" : "disable"}`, null, {
      headers: { ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    logAxiosError("setVaccineActive error", err);
    throw err;
  }
}

// ---------------- Batches dropdown ----------------

export async function listBatchesIdName() {
  try {
    const res = await axios.get(`${BASE_URL}/api/public/batches/all`, { headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("listBatchesIdName error", err);
    throw err;
  }
}

export const listBatches = listBatchesIdName;

// ---------------- Vaccine schedules ----------------

export async function listVaccineSchedules() {
  try {
    const res = await axios.get(SCHEDULES_URL, { headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("listVaccineSchedules error", err);
    throw err;
  }
}

export async function createVaccineSchedule(payload) {
  try {
    const res = await axios.post(SCHEDULES_URL, normalizeSchedulePayload(payload), {
      headers: { ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    logAxiosError("createVaccineSchedule error", err);
    throw err;
  }
}

export async function deleteVaccineSchedule(id) {
  try {
    await axios.delete(`${SCHEDULES_URL}/${id}`, { headers: authHeaders() });
    return true;
  } catch (err) {
    logAxiosError("deleteVaccineSchedule error", err);
    throw err;
  }
}

// Old name kept for old code paths.
export async function assignVaccine(payload) {
  return createVaccineSchedule(payload);
}

export async function assignVaccineToAllOngoing({ vaccineId, projectType, batchGoal, startDate, firstDoseDate }) {
  return createVaccineSchedule({
    vaccineId,
    projectType: projectType || batchGoal,
    applyTo: "ALL",
    firstDoseDate: firstDoseDate || startDate,
  });
}

// ---------------- Vaccine tasks ----------------

export async function listVaccineTasks(batchId, status) {
  const params = {};
  if (batchId) params.batchId = maybeNumber(batchId);
  if (status) params.status = normalizeEnum(status);

  try {
    const res = await axios.get(TASKS_URL, { params, headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("listVaccineTasks error", err);
    throw err;
  }
}

export async function listVaccineTasksByProjectType({ species, projectType, date, from, to, status, taskType, batchId } = {}) {
  const params = {};
  if (batchId) params.batchId = maybeNumber(batchId);
  if (date) params.date = normalizeDate(date);
  if (taskType) params.taskType = normalizeEnum(taskType);
  if (status) params.status = normalizeEnum(status);

  // The new endpoint filters by batch/date/status/type. Species/project reports still work from old backend if needed;
  // the new UI normally uses these fields only for schedule creation.
  try {
    let res = await axios.get(TASKS_URL, { params, headers: authHeaders() });
    let rows = Array.isArray(res.data) ? res.data : [];
    if (species || projectType || from || to) {
      const sp = species ? normalizeEnum(species) : null;
      const pt = projectType ? normalizeEnum(projectType) : null;
      const start = normalizeDate(from);
      const end = normalizeDate(to);
      rows = rows.filter((r) => {
        const d = normalizeDate(r.dueDate || r.taskDate);
        const rowSpecies = normalizeEnum(r.species);
        const rowProject = normalizeEnum(r.projectType);
        const projectMatches = !pt || !rowProject || rowProject === pt ||
          (pt === "FATTENING" && rowProject === "MEAT") || (pt === "MEAT" && rowProject === "FATTENING");
        return (!sp || !rowSpecies || rowSpecies === sp) &&
          projectMatches &&
          (!start || d >= start) &&
          (!end || d <= end);
      });
    }
    return rows;
  } catch (err) {
    logAxiosError("listVaccineTasksByProjectType error", err);
    throw err;
  }
}

export async function nextVaccineTask(batchId) {
  const rows = await listVaccineTasks(batchId);
  return rows.find((r) => ["PENDING", "UPCOMING"].includes(r.status)) || rows[0] || null;
}

export async function completeVaccineTask(taskId) {
  return markVaccineTaskGiven(taskId);
}

export async function markVaccineTaskGiven(taskId) {
  try {
    const res = await axios.patch(`${TASKS_URL}/${taskId}/given`, null, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    logAxiosError("markVaccineTaskGiven error", err);
    throw err;
  }
}

export async function markVaccineTasksGiven(taskIds) {
  try {
    const res = await axios.patch(`${TASKS_URL}/bulk-given`, { taskIds }, { headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("markVaccineTasksGiven error", err);
    throw err;
  }
}

export async function skipVaccineTask(taskId) {
  try {
    const res = await axios.patch(`${TASKS_URL}/${taskId}/skip`, null, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    logAxiosError("skipVaccineTask error", err);
    throw err;
  }
}

export async function skipVaccineTasks(taskIds) {
  try {
    const res = await axios.patch(`${TASKS_URL}/bulk-skip`, { taskIds }, { headers: authHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    logAxiosError("skipVaccineTasks error", err);
    throw err;
  }
}

const vaccineService = {
  listVaccines,
  createVaccine,
  updateVaccine,
  setVaccineActive,
  listBatchesIdName,
  listBatches,
  listVaccineSchedules,
  createVaccineSchedule,
  deleteVaccineSchedule,
  assignVaccine,
  assignVaccineToAllOngoing,
  listVaccineTasks,
  listVaccineTasksByProjectType,
  nextVaccineTask,
  completeVaccineTask,
  markVaccineTaskGiven,
  markVaccineTasksGiven,
  skipVaccineTask,
  skipVaccineTasks,
};

export default vaccineService;
